import { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import IntlTelInput from "react-intl-tel-input";
import AllCountries from "react-intl-tel-input/dist/components/AllCountries";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import PropTypes from "prop-types";
import {
  NOT_SUPPORTED_COUNTRY_CODES,
  RouteName,
  SEND_LINK_TYPES,
  UK_COUNTRY_CODE_NEW,
  UK_COUNTRY_CODE_OLD,
  UK_COUNTRY_DIALCODE,
  allowedCountries,
  allowedCountriesDialCodePattern,
} from "../../constants/Constants";
import { Localize } from "./Localize";
import history from "../../history";
import { handleErrorMsgToastVisiblity, openUrlInBrowser, resetDataForSession } from "../../shared/utility";
import {
  createSessionReset,
  inviteSession,
  inviteSessionReset,
} from "../../actions/sessionListAction";
import storage from "../../services/storage";
import { ButtonWithLoader } from "../UI/Button/Button";
import CustomToast from "../UI/customToast";
import { announce } from "../UI/LiveRegion";
import { Localize as AriaLocalize } from "../../shared/ariaLabelLocalize";
import ContactPicker from "../AddressBook/ContactPicker";
import AddEditContactModal from "../AddressBook/ContactModal/AddEditContactModal";
import useContactsPermissions from "../AddressBook/useContactsPermissions";

// Dial-code → ISO2 lookup (same approach as AddEditContactModal).
// Pins "44" → "gb" since AllCountries lists Guernsey first for +44.
const _dialCodeToIso = {};
AllCountries.getCountries().forEach((c) => {
  if (!_dialCodeToIso[c.dialCode]) _dialCodeToIso[c.dialCode] = c.iso2;
});
_dialCodeToIso["44"] = "gb";
const getIsoByDialCode = (dialCode) => _dialCodeToIso[String(dialCode).replace(/^\+/, "")] || null;

const SendMsg = ({
  id,
  ariaLabelledby,
  type,
  message,
  isDefaultInvite,
  sessionDetailData,
  sendUrl,
  agentUrl,
  defaultCountry,
  isUploadOnlySessionValue,
  mobileNumberRef,
  countryRef,
  isInviteAPICalledRef,
  updateNordicCountryMessage,
  isSessionOwner,
  isForSaskTail,
  selectedContact,
  onSelectContact,
  onClearContact,
  pickerCache,
  onPickerCacheUpdate,
  inviteTargetSlot,
}) => {
  const dispatch = useDispatch();
  const { inviteSessionsLoading, inviteSessionsData, inviteSessionsError } =
    useSelector((state) => state.sessionList);
  const { language } = useSelector((state) => state);
  const intlTelInputRef = useRef(null);

  // ─── Known third-party a11y findings on the country dropdown ───────────────
  // react-intl-tel-input v18 emits the country picker as
  //   <ul class="country-list">
  //     <li class="country">…preferred countries…</li>
  //     <div class="divider"></div>          ← invalid list child
  //     <li class="country">…all countries…</li>
  //   </ul>
  // and the <ul> is scrollable but not keyboard-focusable. This trips two axe
  // rules on the SendCode > SMS SMS country dropdown open state:
  //   1) list — "<ul> must only directly contain <li>, <script> or <template>"
  //   2) scrollable-region-focusable — "scrollable region must have keyboard access"
  //
  // Runtime patching was attempted (MutationObserver to convert div→li and add
  // tabindex="0") but reverted for two reasons:
  //   • The library uses React.createElement + shouldComponentUpdate for the
  //     dropdown, so each open/re-render replaces our <li> with a fresh <div>;
  //     axe still catches the transient invalid state.
  //   • Forcing tabindex on the <ul> hijacks focus away from the library's own
  //     arrow-key navigation on the input, breaking Up/Down keyboard traversal.
  //
  // Both findings are LIBRARY-LEVEL. Not blocking A11Y-22 (fix scope was
  // duplicate IDs + well-formed HTML per tracker). Logged in deferred defects
  // for a future task — options are (a) replace library, (b) patch fork of
  // react-intl-tel-input, (c) accept + document in VPAT under 4.1.1 as
  // third-party limitation.
  // ───────────────────────────────────────────────────────────────────────────
  // Tracks the user's manually-chosen country (not overwritten when a contact is selected)
  const manualCountryDataRef = useRef(countryRef.current);
  // Prevents the synthetic onPhoneNumberChange (triggered by programmatic value update) from
  // overriding the button state that was correctly set by the contact-selection effect

  // Prevents the synthetic onSelectFlag (triggered by programmatic setFlag call) from
  // overriding the button state set by contact selection
  const skipNextFlagSelectRef = useRef(false);
  // When the contact is cleared because the user manually edited the phone number,
  // skip the selectedContact effect so it doesn't wipe out the manually-entered value.
  const skipContactClearEffectRef = useRef(false);
  // Tracks whether the phone number change was triggered by user typing (true)
  // vs programmatic update (false). Set via native 'input' event on the tel input.
  const userTypingRef = useRef(false);

  const [isValidValue, setIsValidValue] = useState(true);
  const [isButtonsDisabled, setIsButtonsDisabled] = useState(true);
  const [isUserInvited, setIsUserInvited] = useState(false);
  const [inviteButton, setInviteButton] = useState("");
  const [localisedValues, setLocalisedValues] = useState({});
  const [mobileNumber, setMobileNumber] = useState(mobileNumberRef.current);
  const [countryData, setCountryData] = useState(countryRef.current);
  const [isInviteAPICalled, setIsInviteAPICalled] = useState(
    isInviteAPICalledRef.current
  );
  const [showSaveContactModal, setShowSaveContactModal] = useState(false);
  const { canAdd, isContactsEnabled } = useContactsPermissions();

  const inviteTypeSuccessMessage =
    type === SEND_LINK_TYPES.SMS.name
      ? localisedValues["sms_sent_label"]
      : localisedValues["whatsapp_sent_label"];

  // Native 'input' events only fire on real user keyboard interaction,
  // not on programmatic value changes. Set a flag so onPhoneNumberChangeHandler
  // can distinguish user typing from synthetic (ref-driven) changes.
  useEffect(() => {
    const tel = intlTelInputRef.current?.tel;
    if (!tel) return;
    const handler = () => { userTypingRef.current = true; };
    tel.addEventListener("input", handler);
    return () => tel.removeEventListener("input", handler);
  }, []);

  useEffect(() => {
    mobileNumberRef.current = mobileNumber;
  }, [mobileNumber]);

  useEffect(() => {
    countryRef.current = countryData;
    type === "SMS" && updateNordicCountryMessage(countryData);
  }, [countryData]);

  useEffect(() => {
    isInviteAPICalledRef.current = isInviteAPICalled;
  }, [isInviteAPICalled]);

  useEffect(() => {
    const curLang = language.selectedlanguage;
    setLocalisedValues(Localize[curLang.id]);
  }, [language.selectedlanguage]);

  // Update mobile number and country code when selected contact changes
  useEffect(() => {
    if (selectedContact?.phone) {
      // Capture the actual current country before overwriting it (even if countryData state is still "")
      const currentCountry = intlTelInputRef.current?.selectedCountryData;
      if (currentCountry?.iso2) manualCountryDataRef.current = currentCountry;

      setMobileNumber(selectedContact.phone);
      // Update country flag and dial code to match the contact.
      // Convert "uk" → "gb" since react-intl-tel-input only recognizes "gb" for United Kingdom.
      if (selectedContact.countryCode || selectedContact.dialCode) {
        const rawIso2 = (selectedContact.countryCode || "").toLowerCase();
        const convertedIso2 = rawIso2 === UK_COUNTRY_CODE_NEW ? UK_COUNTRY_CODE_OLD : rawIso2;
        // If stored countryCode is a dial-code number (e.g. "46") rather than ISO2 ("se"),
        // fall back to dial-code lookup so the correct flag is shown.
        const iso2 = (convertedIso2 && allowedCountries.includes(convertedIso2))
          ? convertedIso2
          : getIsoByDialCode(selectedContact.dialCode) || convertedIso2;
        // Skip the synthetic onSelectFlag so it doesn't override button state before
        // the phone value has updated. We re-validate below via setTimeout instead.
        skipNextFlagSelectRef.current = true;
        intlTelInputRef.current?.setFlag(iso2);
        if (selectedContact.dialCode) {
          const cleanDialCode = selectedContact.dialCode.replace(/^\+/, "");
          setCountryData(prev => ({ ...prev, iso2, dialCode: cleanDialCode }));
        }
      }
      // Re-validate after IntlTelInput has settled with the new country + value.
      // This correctly enables buttons for valid contacts and keeps them disabled
      // for contacts with invalid phone numbers.
      setTimeout(() => {
        const phone = selectedContact.phone;
        const isValid = intlTelInputRef.current?.isValidNumber(phone);
        const cd = intlTelInputRef.current?.selectedCountryData;
        const isValidCustomArea = isPhoneNumberValidForCustomAreaCode(
          cd?.dialCode,
          intlTelInputRef.current?.getNumber(phone, "-")
        );
        setIsButtonsDisabled(!isValid && !isValidCustomArea);
        intlTelInputRef.current?.tel?.focus();
      }, 100);
    } else if (selectedContact === null) {
      // If the contact was cleared because the user manually edited the phone number,
      // do not reset the number or buttons — the user's input should be preserved.
      if (skipContactClearEffectRef.current) {
        skipContactClearEffectRef.current = false;
        return;
      }
      setMobileNumber("");
      setIsButtonsDisabled(true);
      // Restore the country the user had before a contact was selected
      const restoreCountry = manualCountryDataRef.current;
      const defaultIso2 = typeof defaultCountry === "string" ? defaultCountry : defaultCountry?.iso2;
      if (restoreCountry?.iso2) {
        intlTelInputRef.current?.setFlag(restoreCountry.iso2);
        setCountryData(restoreCountry);
      } else if (defaultIso2) {
        intlTelInputRef.current?.setFlag(defaultIso2);
        setCountryData({ iso2: defaultIso2, dialCode: "" });
      }
    }
  }, [selectedContact]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (inviteSessionsLoading) return;

    if (inviteSessionsData) {
      if (isUploadOnlySessionValue) {
        CustomToast.success(
          `${inviteTypeSuccessMessage} ${localisedValues["recipients_can_upload_files"]}`
        );
        history.replace(RouteName.SESSIONS);
      } else {
        CustomToast.success(localisedValues["invite_sent_successfully"]);
      }
      intlTelInputRef.current?.tel?.focus();
      if (!isEmpty(countryData)) {
        storage.set(type + "CountrySelected", countryData)
      }
      setMobileNumber("");
      setIsUserInvited(true);
      setIsInviteAPICalled(true);
      setIsButtonsDisabled(true);
      if (onClearContact) onClearContact();
      if (!isForSaskTail) dispatch(createSessionReset());

      if (inviteButton === "InviteAndJoin") {
        openUrlInBrowser(agentUrl, "_self");
        resetDataForSession();
      }

    } else if (inviteSessionsError) {
      handleErrorMsgToastVisiblity(inviteSessionsError.message);
    }
    dispatch(inviteSessionReset());
  }, [inviteSessionsLoading, inviteSessionsData, inviteSessionsError]);

  const isPhoneNumberValidForCustomAreaCode = (countryCode, phoneNumber) => {
    if (!phoneNumber) return false;
    // phone number validation for digit only and with length of 10 character exact
    // Extract area code from the phone number
    const area_code = phoneNumber.substring(0, 3);
    // Check if country code exists
    const country_index = NOT_SUPPORTED_COUNTRY_CODES.indexOf(countryCode);
    if (country_index >= 0) {
      // fix for 31515 - for australia the number should be 9  or 10 digit
      if (countryCode == "61" && phoneNumber?.replace(/\D+/g, "").length < 9) { return false }
      //fix for 31515 - for countries other than australia the number should be 10 digit
      if (countryCode != "61" && phoneNumber?.replace(/\D+/g, "").length !== 10) { return false };
      // Check if area code exists for the found country code 
      // Removed the array to make sure no new request comes from the customer and for now wil bypass vailidation
      if (area_code >= 200 && area_code <= 999) {
        return true;
      }
    }

    return false;
  }

  const setButtonsDisabled = (isValidPhoneNumber, countryData, value, checkForError = false) => {
    const isValidForCustomAreaCode = isPhoneNumberValidForCustomAreaCode(countryData.dialCode, intlTelInputRef?.current?.getNumber(value, "-"));
    setIsButtonsDisabled(!isValidForCustomAreaCode ? !isValidPhoneNumber : false);
    if (isValidForCustomAreaCode) setCountryData(countryData);
    if (checkForError) setIsValidValue(isValidPhoneNumber || isValidForCustomAreaCode);
  }

  const onPhoneNumberChangeHandler = (_isValid, value, countryData) => {
    // If a contact was selected and the user is physically typing a new number,
    // clear the selected contact (but skip the effect that would wipe the input).
    if (selectedContact && onClearContact && userTypingRef.current) {
      skipContactClearEffectRef.current = true;
      onClearContact();
    }
    userTypingRef.current = false;
    setIsUserInvited(false);
    const isValidPhoneNumber = intlTelInputRef.current?.isValidNumber(value);
    if (isValidPhoneNumber) {
      setCountryData(countryData);
      const numberInput = document.getElementById("numberInput");
      numberInput?.addEventListener("keyup", (event) => {
        if (event.key === "Enter") {
          const actionButton = document.getElementById("messageActionButton");
          if (actionButton) actionButton.click();
        }
      });
    }
    let phoneNumber = value;
    let patternMob = /[a-zA-Z`~!@$%^&_|\=?;:'" ,<>\{\}\\\/]/gi;
    let isnonvalid = patternMob.test(phoneNumber);
    if (isnonvalid) {
      phoneNumber = phoneNumber.replace(patternMob, "");
      setMobileNumber(phoneNumber);
    } else {
      setMobileNumber(phoneNumber);
    }

    let matchedCountryCode = phoneNumber.match(allowedCountriesDialCodePattern);
    if (matchedCountryCode && matchedCountryCode.length > 0) {
      const newValueIndex = phoneNumber.lastIndexOf("+");
      phoneNumber = phoneNumber.substring(newValueIndex);
      matchedCountryCode = phoneNumber.match(allowedCountriesDialCodePattern);
      setMobileNumber(phoneNumber.substring(matchedCountryCode[0]?.length));
    }
    setButtonsDisabled(isValidPhoneNumber, countryData, value);
  };

  const onPhoneNumberFocusHandler = (isValid, value, countryData) => {
    const checkForError = value?.replace(/\D+/g, "").length !== 0;
    setButtonsDisabled(isValid, countryData, value, checkForError);
  };

  const onSelectFlagHandler = (
    _currentNumber,
    countryData,
    _fullNumber,
    isValid
  ) => {
    if (skipNextFlagSelectRef.current) {
      skipNextFlagSelectRef.current = false;
      return;
    }
    setButtonsDisabled(isValid, countryData, intlTelInputRef?.current?.state.value);
    setCountryData(countryData);
    manualCountryDataRef.current = countryData;
  };

  // Save as New Contact handlers
  const handleOpenSaveContact = () => {
    setShowSaveContactModal(true);
  };

  const handleCloseSaveContact = () => {
    setShowSaveContactModal(false);
  };

  const handleSaveContactSuccess = ({ savedContact } = {}) => {
    if (showSaveContactModal) CustomToast.success(localisedValues["contact_added"]);
    setShowSaveContactModal(false);
    if (savedContact && onSelectContact) {
      onSelectContact(savedContact);
    }
  };

  const showSaveAsContact = !isButtonsDisabled && !selectedContact;
  const isSaveAsContactVisible = showSaveAsContact && canAdd && isContactsEnabled;

  useEffect(() => {
    if (isSaveAsContactVisible) announce((AriaLocalize[language?.selectedlanguage?.id] || AriaLocalize['en_US']).save_as_new_contact_available);
  }, [isSaveAsContactVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const inviteSesssionHandler = (buttonType) => {
    const countryIsoCode = countryData.dialCode === UK_COUNTRY_DIALCODE ? UK_COUNTRY_CODE_NEW : countryData?.iso2;

    if (isValidValue) {
      const request = {
        sessionId: sessionDetailData.session?.sessionId,
        isoCode: countryIsoCode?.toUpperCase(),
        CountryCode: countryData?.dialCode,
        mobileNo: mobileNumber?.replace(/\D+/g, ""),
        url: sendUrl,
      };
      if (type === "SMS") {
        request.invitationType = 1;
        request.message = message;
      } else if (type === "WhatsApp") {
        request.invitationType = 2;
      }
      if (selectedContact?.contactId) {
        request.contactId = selectedContact.contactId;
      }
      dispatch(inviteSession(request));
      setInviteButton(buttonType);
    }
  };

  return (
    <div
      className={clsx("tab-pane", isDefaultInvite ? "active show" : "")}
      id={id}
      data-testid={id}
      role="tabpanel"
      aria-labelledby={ariaLabelledby}
    >
      <div className="guest-invite-box">
        <div className="guest-invite-body">
          <div className="title">
            <h2 className="title-mob">
              {type === SEND_LINK_TYPES.SMS.name
                ? localisedValues["sms_label"]
                : localisedValues["whatsapp_label"]}
            </h2>
            <h2 className="title-desktop">
              {localisedValues["enter_mobile_number"]}
            </h2>
          </div>
          {/* Address Book Contact Picker */}
          {onSelectContact && (
            <ContactPicker
              selectedContact={selectedContact}
              onSelectContact={onSelectContact}
              onClearContact={onClearContact}
              currentPhone={mobileNumber}
              currentDialCode={countryData?.dialCode}
              currentCountryCode={countryData?.iso2}
              pickerCache={pickerCache}
              onPickerCacheUpdate={onPickerCacheUpdate}
            />
          )}
          <div className="form-group mobile-number-group">
            <label htmlFor="numberInput" className="visually-hidden">
              {localisedValues["mobile_number"]}
            </label>
            <div className="form-group-in">
              <IntlTelInput
                ref={intlTelInputRef}
                fieldId="numberInput"
                data-testid="numberInput"
                defaultCountry={defaultCountry || "us"}
                // Intentional: focus first field when the invite panel opens.
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={true}
                value={mobileNumber}
                containerClassName="intl-tel-input"
                inputClassName="form-control form-control-grey height-46 focus-font-bold"
                separateDialCode={true}
                onlyCountries={allowedCountries}
                autoHideDialCode={true}
                placeholder={localisedValues["mobile_number"]}
                onPhoneNumberChange={onPhoneNumberChangeHandler}
                onPhoneNumberBlur={onPhoneNumberFocusHandler}
                onPhoneNumberFocus={() => setIsValidValue(true)}
                onSelectFlag={onSelectFlagHandler}
                useMobileFullscreenDropdown={false}
                telInputProps={{
                  autoComplete: "tel-national",
                  "aria-required": "true",
                  "aria-invalid": !isValidValue || undefined,
                  "aria-describedby": !isValidValue ? "numberInput-error" : undefined,
                }}
              />
            </div>
            {!isValidValue && (
              <div id="numberInput-error" role="alert" className="validation-error">
                {localisedValues["enter_valid_mobile"]}
              </div>
            )}
            {isSaveAsContactVisible && (
              <button
                type="button"
                className="btn-save-as-contact"
                onClick={handleOpenSaveContact}
              >
                <span className="for-icon icon-plus-round" aria-hidden="true"></span>
                {localisedValues["save_as_new_contact"]}
              </button>
            )}
          </div>
          {/* Save as New Contact Modal */}
          <AddEditContactModal
            isOpen={showSaveContactModal}
            contact={null}
            onClose={handleCloseSaveContact}
            onSuccess={handleSaveContactSuccess}
            initialPhone={mobileNumber}
            initialDialCode={countryData?.dialCode || intlTelInputRef.current?.selectedCountryData?.dialCode}
            initialCountryCode={countryData?.iso2 || intlTelInputRef.current?.selectedCountryData?.iso2}
          />
          <div className="form-group sms-invite-textarea">
            <div className="form-group-in">
              <textarea
                className="form-control form-control-grey focus-font-bold"
                disabled
                value={message}
                aria-label={type === SEND_LINK_TYPES.SMS.name ? localisedValues["sms_label"] : localisedValues["whatsapp_label"]}
              />
            </div>
          </div>
          {isUserInvited && (
            <div className="send-success">
              <span className="send-icon icon-checked-fill"></span>
              <span className="send-text">{inviteTypeSuccessMessage}</span>
            </div>
          )}
        </div>
        {/* KBD-15 (A11Y-14): Guest / Silent Observer radios injected via slot
            between the panel's fields and the invite footer so Tab flow is
            input → radios → Invite / Invite & Join regardless of whether the
            buttons are enabled. */}
        {inviteTargetSlot}
        <div className="guest-invite-footer">
          <div className="button-inline text-center d-flex">
            <div className="button join-call-btn">
              {isInviteAPICalled && !isUploadOnlySessionValue && isSessionOwner && (
                <div className="join-call-btn-in">
                  <button
                    type="button"
                    className="btn btn-link font-weight-500"
                    onClick={() => { openUrlInBrowser(agentUrl, "_self"); resetDataForSession(); }}
                  >
                    {localisedValues["join_call"]}
                  </button>
                </div>
              )}
            </div>
            {!isUploadOnlySessionValue ? (
              <>
                <div className="button">
                  <ButtonWithLoader
                    className="btn btn-light-primary btn-with-icon font-weight-500 hover-boxshadow loader-sm-space"
                    name={localisedValues["invite"]}
                    disable={isButtonsDisabled}
                    showLoader={
                      inviteButton === "Invite" && inviteSessionsLoading
                    }
                    onClick={() => inviteSesssionHandler("Invite")}
                  />
                </div>
                {isSessionOwner && <div className="button">
                  <ButtonWithLoader
                    id="messageActionButton"
                    className="btn btn-primary btn-with-icon font-weight-500 hover-boxshadow loader-sm-space"
                    name={localisedValues["invite_and_join"]}
                    disable={isButtonsDisabled}
                    showLoader={
                      inviteButton === "InviteAndJoin" && inviteSessionsLoading
                    }
                    onClick={() => inviteSesssionHandler("InviteAndJoin")}
                  />
                </div>}
              </>
            ) : (
              <div className="button">
                <ButtonWithLoader
                  id="messageActionButton"
                  className="btn btn-primary btn-with-icon font-weight-500 hover-boxshadow loader-sm-space"
                  name={localisedValues["send_link"]}
                  disable={isButtonsDisabled}
                  showLoader={
                    inviteButton === "Invite" && inviteSessionsLoading
                  }
                  onClick={() => inviteSesssionHandler("Invite")}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

SendMsg.propTypes = {
  id: PropTypes.string,
  ariaLabelledby: PropTypes.string,
  type: PropTypes.string,
  message: PropTypes.string,
  isDefaultInvite: PropTypes.bool,
  sessionDetailData: PropTypes.object,
  sendUrl: PropTypes.string,
  agentUrl: PropTypes.string,
  defaultCountry: PropTypes.string,
  isUploadOnlySessionValue: PropTypes.bool,
  mobileNumberRef: PropTypes.object,
  countryRef: PropTypes.object,
  isInviteAPICalledRef: PropTypes.object,
  updateNordicCountryMessage: PropTypes.func,
  selectedContact: PropTypes.shape({
    contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    phone: PropTypes.string,
    dialCode: PropTypes.string,
    countryCode: PropTypes.string,
    email: PropTypes.string,
    isFavorite: PropTypes.bool,
  }),
  onSelectContact: PropTypes.func,
  onClearContact: PropTypes.func,
  pickerCache: PropTypes.shape({
    items: PropTypes.array,
    totalCount: PropTypes.number,
  }),
  onPickerCacheUpdate: PropTypes.func,
  isForSaskTail: PropTypes.bool,
  isSessionOwner: PropTypes.bool,
  inviteTargetSlot: PropTypes.node,
};

export default SendMsg;
